"use client";

import JourneyHero from "@/components/journey/JourneyHero";
import PricingHero from "@/components/pricing/PricingHero";
import DownloadHero from "@/components/download/DownloadHero";
import JourneySocialProof from "@/components/journey/JourneySocialProof";
import JourneyFeaturesGrid from "@/components/journey/JourneyFeaturesGrid";
import JourneyTabs from "@/components/journey/JourneyTabs";
import JourneyTestimonials from "@/components/journey/JourneyTestimonials";
import PricingCards from "@/components/pricing/PricingCards";
import BasicTestimonials from "@/components/shared/BasicTestimonials";
import PricingFAQ from "@/components/pricing/PricingFAQ";
import DeviceDownloadGrid from "@/components/download/DeviceDownloadGrid";
import HowToSection from "@/components/download/HowToSection";
import BenefitsGrid from "@/components/download/BenefitsGrid";
import ComparisonSection from "@/components/download/ComparisonSection";
import DownloadFAQ from "@/components/download/DownloadFAQ";
import DownloadCTA from "@/components/download/DownloadCTA";
import PolicyPage from "@/components/legal/PolicyPage";
import RichText from "@/components/seo/RichText";
import StatsBar from "@/components/seo/StatsBar";
import IconFeatureList from "@/components/seo/IconFeatureList";
import CtaBanner from "@/components/seo/CtaBanner";
import LogoCloud from "@/components/seo/LogoCloud";
import TwoColumn from "@/components/seo/TwoColumn";
import AccordionSection from "@/components/seo/AccordionSection";
import HeroSimple from "@/components/seo/HeroSimple";
import HeroSplit from "@/components/seo/HeroSplit";
import BentoGrid from "@/components/seo/BentoGrid";
import DataTable from "@/components/seo/DataTable";
import FeatureCards from "@/components/seo/FeatureCards";
import AlternatingFeatures from "@/components/seo/AlternatingFeatures";
import FaqTwoColumn from "@/components/seo/FaqTwoColumn";
import NumberList from "@/components/seo/NumberList";
import HeroGradient from "@/components/seo/HeroGradient";
import HeroVideo from "@/components/seo/HeroVideo";
import HeroMinimal from "@/components/seo/HeroMinimal";
import StatsCards from "@/components/seo/StatsCards";
import StatsInline from "@/components/seo/StatsInline";
import FeatureListCards from "@/components/seo/FeatureListCards";
import FeatureShowcase from "@/components/seo/FeatureShowcase";
import TableOfContents from "@/components/seo/TableOfContents";
import RelatedPages from "@/components/seo/RelatedPages";
import PressQuotes from "@/components/seo/PressQuotes";
import TrustBadges from "@/components/seo/TrustBadges";
import ChecklistComparison from "@/components/seo/ChecklistComparison";
import VideoEmbed from "@/components/seo/VideoEmbed";
import CalloutBlock from "@/components/seo/CalloutBlock";
import ServerLocations from "@/components/seo/ServerLocations";
import PlatformDownloads from "@/components/seo/PlatformDownloads";
import BreadcrumbsNav from "@/components/seo/BreadcrumbsNav";
import MasonryGrid from "@/components/seo/MasonryGrid";

// Component mapping - all client components
const COMPONENT_MAP = {
  "hero": JourneyHero,
  "pricing-hero": PricingHero,
  "download-hero": DownloadHero,
  "social-proof": JourneySocialProof,
  "features-grid": JourneyFeaturesGrid,
  "tabs": JourneyTabs,
  "testimonials": JourneyTestimonials,
  "pricing-cards": PricingCards,
  "basic-testimonials": BasicTestimonials,
  "pricing-faq": PricingFAQ,
  "device-download-grid": DeviceDownloadGrid,
  "how-to-section": HowToSection,
  "benefits-grid": BenefitsGrid,
  "comparison-section": ComparisonSection,
  "download-faq": DownloadFAQ,
  "download-cta": DownloadCTA,
  "policy-page": PolicyPage,
  "legal-page": PolicyPage, // Legacy type
  "rich-text": RichText,
  "stats-bar": StatsBar,
  "icon-feature-list": IconFeatureList,
  "cta-banner": CtaBanner,
  "logo-cloud": LogoCloud,
  "two-column": TwoColumn,
  "accordion-section": AccordionSection,
  "hero-simple": HeroSimple,
  "hero-split": HeroSplit,
  "bento-grid": BentoGrid,
  "data-table": DataTable,
  "feature-cards": FeatureCards,
  "alternating-features": AlternatingFeatures,
  "faq-two-column": FaqTwoColumn,
  "number-list": NumberList,
  "hero-gradient": HeroGradient,
  "hero-video": HeroVideo,
  "hero-minimal": HeroMinimal,
  "stats-cards": StatsCards,
  "stats-inline": StatsInline,
  "feature-list-cards": FeatureListCards,
  "feature-showcase": FeatureShowcase,
  "table-of-contents": TableOfContents,
  "related-pages": RelatedPages,
  "press-quotes": PressQuotes,
  "trust-badges": TrustBadges,
  "checklist-comparison": ChecklistComparison,
  "video-embed": VideoEmbed,
  "callout-block": CalloutBlock,
  "server-locations": ServerLocations,
  "platform-downloads": PlatformDownloads,
  "breadcrumbs-nav": BreadcrumbsNav,
  "masonry-grid": MasonryGrid,
} as const;

interface DynamicComponentRendererProps {
  type: string;
  props: any;
}

export function DynamicComponentRenderer({ type, props }: DynamicComponentRendererProps) {
  const Component = COMPONENT_MAP[type as keyof typeof COMPONENT_MAP];

  if (!Component) {
    console.warn(`Unknown component type: ${type}`);
    return null;
  }

  return <Component {...props} />;
}
